import { colors, radius, fontStack } from '../design-system/tokens';

/**
 * Shared auth helpers + visual styles. Used by Login, Signup,
 * ForgotPassword, and ResetPassword so all four pages stay
 * visually identical.
 */

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PASSWORD_MIN = 8;

/**
 * Translate the most common Supabase / network errors into copy
 * a user can act on. Anything we don't recognise passes through.
 */
export function friendlyAuthError(message) {
  const m = String(message || '').toLowerCase();
  if (m.includes('invalid login credentials'))
    return 'That email and password did not match. Try again or reset your password.';
  if (m.includes('user already registered'))
    return 'An account already exists for that email. Try signing in instead.';
  if (m.includes('email not confirmed'))
    return 'Please confirm your email first. Check your inbox for the verification link.';
  if (m.includes('failed to fetch') || m.includes('networkerror'))
    return 'Network seems unreachable. Check your connection and try again.';
  if (m.includes('rate limit') || m.includes('429'))
    return 'Too many attempts. Wait a moment and try again.';
  if (m.includes('jwt') || m.includes('expired') || m.includes('invalid token'))
    return 'This link is invalid or has expired. Request a new one to continue.';
  if (m.includes('same as the existing'))
    return 'New password must be different from your current one.';
  if (m.includes('password should be at least'))
    return `Password should be at least ${PASSWORD_MIN} characters.`;
  return message || 'Something went wrong. Please try again.';
}

/* ================================================================
   Shared visual styles — keep all four auth pages identical.
   ================================================================ */

export const inputStyle = {
  width: '100%',
  padding: '12px 14px',
  minHeight: 44,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  fontSize: 15,
  fontFamily: fontStack,
  color: colors.textPrimary,
  background: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 120ms ease, box-shadow 120ms ease',
};

export const inputFocusStyle = {
  borderColor: colors.primary,
  boxShadow: `0 0 0 3px rgba(47, 91, 255, 0.16)`,
};

export const inputErrorStyle = {
  borderColor: 'var(--danger)',
};

export const labelStyle = {
  display: 'block',
  fontSize: 14,
  fontWeight: 500,
  color: colors.textSecondary,
  marginBottom: 6,
  fontFamily: fontStack,
};

export const inlineErrorStyle = {
  margin: '6px 0 0',
  color: 'var(--danger)',
  fontSize: 12,
  fontFamily: fontStack,
};

export const primaryButtonStyle = (disabled = false, loading = false) => ({
  display: 'block',
  width: '100%',
  padding: '12px 24px',
  minHeight: 44,
  borderRadius: radius.md,
  border: 'none',
  background: disabled ? '#9CB1FF' : colors.primary,
  color: '#fff',
  fontSize: 16,
  fontWeight: 600,
  fontFamily: fontStack,
  cursor: disabled ? 'not-allowed' : (loading ? 'wait' : 'pointer'),
  transition: 'background-color 120ms ease, opacity 120ms ease',
  opacity: loading ? 0.85 : 1,
});

export const linkStyle = {
  color: colors.primary,
  fontFamily: fontStack,
  fontWeight: 500,
  textDecoration: 'none',
};

export const subtleLinkStyle = {
  color: colors.textSecondary,
  fontFamily: fontStack,
  fontSize: 13,
  textDecoration: 'none',
};
